import { Meta, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import { format } from 'date-fns';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LicensePlate, UnifiedPermit } from 'types/generatedSchemaTypes';
import { isSalesOrderFree } from 'utils/commonUtils';
import { shouldShowSalesOrderDoorCode } from 'utils/permitUtils';
import { makePoint } from 'utils/mapUtils';
import LocationRow from '../permitRows/LocationRow';
import ParkingSpaceRow from '../permitRows/ParkingSpaceRow';
import PaymentMethodRow from '../permitRows/PaymentMethodRow';
import TicketRow from '../permitRows/TicketRow';
import ValidityDateRow from '../permitRows/ValidityDateRow';
import VehicleRow from '../permitRows/VehicleRow';
import { SalesOrderReceipt } from '../SalesOrderReceiptClass';
import { RowContainer } from '../styles';

const SalesOrderPermit = ({
  permit,
  onPress,
}: {
  permit: UnifiedPermit;
  onPress?: () => void;
}) => {
  const { t } = useTranslation();
  const salesOrder =
    permit.permitType === 'SALES_ORDER' ? new SalesOrderReceipt(permit) : null;

  const paymentCard = useSelectedPaymentMethod(
    salesOrder?.creditCardId ?? undefined
  );

  if (!salesOrder) {
    return null;
  }

  const {
    uid,
    orderLines,
    orderStatus,
    isWaiting,
    nextBillingDate,
    startDate,
    endDate,
    isInvoicing,
    isRecurring,
    billingType: billType,
    paymentMethod,
    usageType,
  } = salesOrder;

  const { pmc, billingFrequency, parkingSpaces } = orderLines?.[0] || {};
  const registrationPlates = (orderLines?.[0]?.registrationPlates ?? []).filter(
    (lp): lp is LicensePlate => lp !== null
  );

  const { parkZone, prices } = pmc || {};

  const marker = parkZone?.address?.marker;
  const markerCoordinate = marker ? makePoint(marker.coordinates) : undefined;
  const street = parkZone?.address?.street ?? t('parkingZone.noAddress');
  const number = parkZone?.address?.number || '';
  const freeSalesOrder = isSalesOrderFree(prices);
  const billingFrequencyValue =
    typeof billingFrequency === 'string' ? billingFrequency : '';
  const isWaitingCSR = orderStatus === 'WAITING_FOR_CSR_APPROVAL';

  return (
    <>
      <LocationRow
        uid={uid}
        street={street}
        number={number}
        markerCoordinate={markerCoordinate}
      />
      {shouldShowSalesOrderDoorCode({
        doorCode: parkZone?.info?.doorCode,
        salesOrder: permit,
      }) && (
        <TicketRow
          title={t('longTermParking.garageEntryCode')}
          content={parkZone.info.doorCode}
          testID={`permit-${uid}-door-code`}
        />
      )}
      <TicketRow
        title={t('services.permitType')}
        content={t(`permit.type.${billingFrequencyValue}`)}
        testID={`permit-${uid}-type`}
      />
      {usageType !== 'SKIDATA' && usageType !== 'CUSTOMER' && (
        <VehicleRow
          uid={uid}
          registrationPlates={registrationPlates}
          permit={permit}
          pmc={pmc}
        />
      )}
      <ParkingSpaceRow
        uid={uid}
        parkingSpaces={parkingSpaces}
        pmc={pmc}
        permit={permit}
        orderLineIdWithVersion={permit.orderLineIdWithVersion}
      />
      <TicketRow
        title={t('services.billing')}
        testID={`permit-${uid}-billing`}
        content={t(
          `services.${
            billType === 'ONE_TIME' ? 'oneTimePayment' : 'recurringPayment'
          }`
        )}
      />
      {!freeSalesOrder && (
        <PaymentMethodRow
          uid={uid}
          paymentMethod={paymentMethod}
          paymentCard={paymentCard}
        />
      )}

      {!isWaitingCSR && (
        <>
          <TicketRow
            title={t('permit.access.validFrom')}
            testID={`permit-${uid}-valid-from`}>
            <RowContainer>
              {startDate && (
                <Meta testID={`permit-${permit.id}-renewal`}>
                  {format(new Date(startDate), 'd.M.y')}
                </Meta>
              )}
            </RowContainer>
          </TicketRow>
          {nextBillingDate && (
            <TicketRow
              title={t('permit.access.validTo')}
              testID={`permit-${uid}-valid-to`}>
              <RowContainer>
                <ValidityDateRow
                  permit={permit}
                  orderStatus={orderStatus}
                  endDate={endDate}
                  isRecurring={isRecurring}
                  isInvoicing={isInvoicing}
                  nextBillingDate={nextBillingDate}
                  uid={uid}
                  isWaiting={isWaiting}
                />
              </RowContainer>
            </TicketRow>
          )}
        </>
      )}
      <TicketRow
        onPress={onPress}
        titleColor={theme.colors.black}
        title={t('services.viewDetails')}
        divider={false}>
        <Icon
          name="Chevron_Right"
          size="s"
          testID={`permit-${uid}-view-details`}
        />
      </TicketRow>
    </>
  );
};

export default SalesOrderPermit;
